/* Zeno — Carousel scroll-position bar (shared)
   ----------------------------------
   Same scrollbar-thumb pattern as the cart upsell carousel
   (assets/cart-upsell.js) — a fixed-width segment representing how
   much of the row is visible, sliding via transform as you scroll,
   NOT a loading-bar style fill that grows from 0% to 100%. Applied to
   any [data-zeno-carousel-wrapper] — currently "You may also like"
   and "Recently viewed". Dragging/clicking the bar itself scrubs the
   carousel directly.

   Also watches for the recently-viewed carousel's cards being
   inserted asynchronously (they're fetched and appended by
   assets/zeno-recently-viewed.js after this script runs) and
   re-measures once they land, since scrollWidth is 0 until then.

   IMPORTANT FIX: "You may also like" (sections/related-products.liquid)
   renders empty on first page load — recommendations.performed is
   only true once Shopify's product-recommendations endpoint responds,
   which the theme's ProductRecommendations custom element (assets/
   global.js) fetches asynchronously via IntersectionObserver and then
   drops in with `this.innerHTML = recommendations.innerHTML`. That
   REPLACES the element's contents wholesale, so the whole
   [data-zeno-carousel-wrapper] div (heading, list, progress track —
   all of it) is a brand-new DOM node that did not exist yet when this
   script's initial initAll() ran at DOMContentLoaded. Without a
   listener specifically watching for that, the newly-inserted wrapper
   never gets its scroll/progress-bar behavior wired up at all — it
   just sits there dead (no bar, no drag) even though the products
   themselves render fine. "Recently viewed" doesn't hit this because
   its wrapper is server-rendered up front (just hidden via a `hidden`
   attribute) and only the LIST ITEMS get appended into it later, so
   the existing per-wrapper MutationObserver below already catches
   that case.
   Fix: a second, document-level MutationObserver watches for any
   NEW [data-zeno-carousel-wrapper] element appearing anywhere on the
   page after the fact (covers the related-products AJAX swap, and any
   future carousel that behaves the same way) and initializes it the
   same way initAll() would have. initWrapper() is idempotent (guarded
   by a data attribute) so this is safe even if a wrapper is both
   present at load and later replaced. */

(function () {
  function initWrapper(wrapper) {
    if (wrapper.dataset.zenoCarouselProgressInit) return;
    wrapper.dataset.zenoCarouselProgressInit = 'true';

    var list = wrapper.querySelector('[data-zeno-carousel]');
    var track = wrapper.querySelector('[data-zeno-carousel-progress-track]');
    var fill = wrapper.querySelector('[data-zeno-carousel-progress-fill]');
    if (!list || !track || !fill) return;

    function update() {
      var scrollable = list.scrollWidth - list.clientWidth;
      var trackWidth = track.clientWidth;
      var thumbPercent = list.scrollWidth > 0 ? Math.min(1, list.clientWidth / list.scrollWidth) : 1;
      var thumbWidth = Math.max(trackWidth * thumbPercent, 24);
      var scrollPercent = scrollable > 0 ? list.scrollLeft / scrollable : 0;
      var maxOffset = trackWidth - thumbWidth;

      fill.style.width = thumbWidth + 'px';
      fill.style.transform = 'translateX(' + scrollPercent * maxOffset + 'px)';
      track.style.display = scrollable > 4 ? '' : 'none'; // nothing to scrub
    }

    list.addEventListener('scroll', update, { passive: true });
    update();

    // Re-measure if content is added after the fact (recently-viewed
    // cards arrive async via fetch).
    var contentObserver = new MutationObserver(update);
    contentObserver.observe(list, { childList: true });

    // Also re-measure on resize, since card sizing is responsive.
    window.addEventListener('resize', update);

    var scrubbing = false;

    function scrubTo(clientX) {
      var rect = track.getBoundingClientRect();
      var percent = (clientX - rect.left) / rect.width;
      percent = Math.max(0, Math.min(1, percent));
      var scrollable = list.scrollWidth - list.clientWidth;
      list.scrollLeft = percent * scrollable;
    }

    track.addEventListener('pointerdown', function (event) {
      scrubbing = true;
      track.setPointerCapture(event.pointerId);
      scrubTo(event.clientX);
    });

    track.addEventListener('pointermove', function (event) {
      if (!scrubbing) return;
      scrubTo(event.clientX);
    });

    function stopScrubbing() {
      scrubbing = false;
    }

    track.addEventListener('pointerup', stopScrubbing);
    track.addEventListener('pointercancel', stopScrubbing);

    // Desktop mouse-drag on the list itself, matching the pattern used
    // elsewhere in the theme (cart upsell, product-card carousels).
    var isPointerDown = false;
    var isDragging = false;
    var dragStartX = 0;
    var dragStartScrollLeft = 0;
    var DRAG_THRESHOLD = 6;

    list.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch') return;
      isPointerDown = true;
      isDragging = false;
      dragStartX = event.clientX;
      dragStartScrollLeft = list.scrollLeft;
    });

    list.addEventListener('pointermove', function (event) {
      if (!isPointerDown) return;
      var delta = event.clientX - dragStartX;
      if (!isDragging && Math.abs(delta) > DRAG_THRESHOLD) {
        isDragging = true;
        list.classList.add('is-dragging');
        list.setPointerCapture(event.pointerId);
      }
      if (isDragging) {
        list.scrollLeft = dragStartScrollLeft - delta;
      }
    });

    function endDrag() {
      isPointerDown = false;
      if (isDragging) {
        isDragging = false;
        list.classList.remove('is-dragging');
      }
    }

    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);
    list.addEventListener('pointerleave', endDrag);

    list.addEventListener(
      'click',
      function (event) {
        if (isDragging) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-zeno-carousel-wrapper]').forEach(initWrapper);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Catches wrappers that don't exist yet at DOMContentLoaded — e.g.
  // "You may also like" (sections/related-products.liquid), whose
  // entire contents (including the wrapper div) only appear once
  // ProductRecommendations (assets/global.js) finishes its async
  // fetch and does `this.innerHTML = recommendations.innerHTML`. Since
  // that swap can happen anywhere under <product-recommendations>,
  // watch the whole document for new matching elements rather than
  // trying to scope this to one container.
  if (window.MutationObserver) {
    var documentObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // element nodes only
          if (node.matches && node.matches('[data-zeno-carousel-wrapper]')) {
            initWrapper(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('[data-zeno-carousel-wrapper]').forEach(initWrapper);
          }
        });
      });
    });
    documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
